import * as Yup from 'yup';
import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { styled } from '@mui/material/styles';
import { Link, Stack, Typography, TextField, IconButton, InputAdornment, Box, Alert, CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotificationsProvider } from '../../context/NotificationsContext';
// component
import Iconify from '../../components/Iconify';
import Logo from '../../components/Logo';
import config from '../../config';

import { acceptInvitation } from './actions'

// ----------------------------------------------------------------------
const LoginStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: {
    height: 1,
    width: 1
  },
  elevation: 5,
}));

const LogoStyle = {
  width: 175,
  height: 48
};

export default function AcceptInvitationForm() {
  const { t } = useTranslation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [validating, setValidating] = React.useState(true);
  const [invitee, setInvitee] = React.useState(null);

  // Get token and email from URL params
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  // Pre-validate the token via GET /auth/validate-invitation. With the
  // Cognito flow the token is a JWT signed with INVITATION_SECRET — the
  // backend verifies signature/expiry/single-use and returns the invitee's
  // email + firstName so we can confirm the right person is at this URL.
  // The legacy flow's bcrypt-token endpoint returns 400 here, in which case
  // we fall back to the form using the URL params (acceptInvitation will
  // then dispatch to whichever backend usecase is configured).
  React.useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError(t('accept-invitation.missing-params'));
      setValidating(false);
      return undefined;
    }
    axios
      .get(`${config.apiBaseUrl}/auth/validate-invitation`, { params: { token } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.valid) {
          setInvitee({ email: data.email, firstName: data.firstName });
        } else {
          setError(t('accept-invitation.error-invalid'));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.data?.error;
        if (code === 'EXPIRED_TOKEN') {
          setError(t('accept-invitation.error-expired'));
        } else if (code === 'TOKEN_ALREADY_USED') {
          setError(t('accept-invitation.error-already-used'));
        } else if (err?.response?.status === 400) {
          // Legacy bcrypt token: validate-invitation rejects it. Fall through
          // to the form so the existing legacy submit path can still work.
          setInvitee({ email: emailFromUrl || '' });
        } else {
          setError(t('accept-invitation.error-default'));
        }
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, emailFromUrl, t]);

  // Password rules align with the Cognito User Pool policy
  // (infra/cognito/serverless.yml): MinimumLength=10, RequireUppercase,
  // RequireLowercase, RequireNumbers, RequireSymbols=false. Keep the
  // client-side checks in sync; if the pool changes, update both.
  const AcceptInvitationSchema = React.useMemo(() => Yup.object().shape({
    email: Yup.string().email(t('accept-invitation.invalid-email')).required(t('accept-invitation.required-email')),
    password: Yup.string()
      .min(10, t('accept-invitation.password-too-short'))
      .matches(/[A-Z]/, t('accept-invitation.password-missing-uppercase'))
      .matches(/[a-z]/, t('accept-invitation.password-missing-lowercase'))
      .matches(/[0-9]/, t('accept-invitation.password-missing-number'))
      .required(t('accept-invitation.required-password')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('accept-invitation.passwords-must-match'))
      .required(t('accept-invitation.required-confirm-password'))
  }), [t]);

  const formik = useFormik({
    initialValues: {
      email: invitee?.email || emailFromUrl || '',
      password: '',
      confirmPassword: '',
    },
    enableReinitialize: true,
    validationSchema: AcceptInvitationSchema,
    onSubmit: (data, { setSubmitting }) => {
      if (!token) {
        setError(t('accept-invitation.missing-token'));
        setSubmitting(false);
        return;
      }
      setError(null);
      dispatch(acceptInvitation(token, data.email, data.password, () => {
        setSubmitting(false);
        showNotification('success', t('accept-invitation.success'));
        navigate('/setup-2fa?from=invitation');
      }, (translationKey) => {
        setSubmitting(false);
        setError(t(translationKey));
      }))
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword((show) => !show);
  };

  if (validating) {
    return (
      <LoginStyle>
        <ContainerStyle>
          <Stack alignItems="center" spacing={3} sx={{ py: 6 }}>
            <Logo sx={LogoStyle} />
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('accept-invitation.validating', 'Validating invitation…')}
            </Typography>
          </Stack>
        </ContainerStyle>
      </LoginStyle>
    );
  }

  return (
    <LoginStyle>
      <ContainerStyle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Stack alignItems="center">
                <Logo sx={LogoStyle} />
              </Stack>
              <Typography variant="h4">
                {invitee?.firstName
                  ? t('accept-invitation.title-greeting', 'Welcome, {{firstName}}', { firstName: invitee.firstName })
                  : t('accept-invitation.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('accept-invitation.subtitle')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                autoComplete="username"
                type="email"
                label={t('accept-invitation.email-label')}
                {...getFieldProps('email')}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                disabled={!!(invitee?.email || emailFromUrl)}
              />

              <TextField
                fullWidth
                autoComplete="new-password"
                type={showPassword ? 'text' : 'password'}
                label={t('accept-invitation.password-label')}
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password ? errors.password : t('accept-invitation.password-requirements')}
              />

              <TextField
                fullWidth
                autoComplete="new-password"
                type={showConfirmPassword ? 'text' : 'password'}
                label={t('accept-invitation.confirm-password-label')}
                {...getFieldProps('confirmPassword')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowConfirmPassword} edge="end">
                        <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
              />

              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting} disabled={!token || !invitee}>
                  {t('accept-invitation.submit-button')}
                </LoadingButton>
                <Link component={RouterLink} variant="subtitle2" to="/forgot-password" underline="hover">
                  {t('accept-invitation.link-expired')}
                </Link>
                <Link component={RouterLink} variant="subtitle2" to="/login" underline="hover">
                  {t('accept-invitation.back-to-login')}
                </Link>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </ContainerStyle>
    </LoginStyle>
  );
};
